import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/useToast';
import { useGenerateIntelligenceReportMutation, IntelligenceReportResponse } from '@/lib/api-extensions/admin-queries';
import { Brain, Search } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom } from '@/store';
import { cn } from '@/lib/utils';

interface IntelligenceReportGeneratorProps {
  className?: string;
  organizationId: number | null;
  businessId: number | null;
}

export function IntelligenceReportGenerator({ className, organizationId, businessId }: IntelligenceReportGeneratorProps) {
  const [response, setResponse] = useState<IntelligenceReportResponse | null>(null);
  const { toast } = useToast();
  
  // Get the currently selected Google Ads account
  const currentGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);
  
  const generateIntelligenceReportMutation = useGenerateIntelligenceReportMutation();
  
  const handleGenerate = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Please select an organization',
        variant: 'destructive',
      });
      return;
    }

    if (!businessId) {
      toast({
        title: 'Error',
        description: 'Please select a business',
        variant: 'destructive',
      });
      return;
    }

    if (!currentGoogleAdsAccount) {
      toast({
        title: 'Error',
        description: 'No Google Ads account is currently selected. Please select a Google Ads account first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await generateIntelligenceReportMutation.mutateAsync({
        organizationId: organizationId,
        googleadsaccountId: parseInt(currentGoogleAdsAccount.id, 10),
        businessId: businessId,
      });
      
      setResponse(result);
      
      toast({
        title: 'Success',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate intelligence report',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="px-6 py-6">
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Generate Intelligence Report
        </CardTitle>
        <CardDescription className="mt-2">
          Generate an AI-powered market intelligence report.
          This report analyzes market trends, competitor positioning, and opportunities using advanced AI models.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6 pt-6">
        
        {/* Report Info */}
        <div className="rounded-lg border bg-gray-50 p-4 space-y-3">
          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <div className="text-sm flex items-center gap-2">
              <span className="text-purple-600">🎯</span>
              <span><strong>Strategic Recommendations:</strong> Actionable insights for market positioning</span>
            </div>
          </div>
          <div className="text-xs text-blue-600">
            Powered by AI web search with real-time data analysis
          </div>
        </div>
        
        {/* Generate Button */}
        <Button 
          onClick={handleGenerate}
          disabled={generateIntelligenceReportMutation.isPending || !organizationId || !businessId || !currentGoogleAdsAccount}
          className="w-full"
        >
          {generateIntelligenceReportMutation.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Generating Intelligence Report...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              Generate Intelligence Report
            </>
          )}
        </Button>
        
        {/* Estimated Time */}
        <div className="text-center">
          <p className="text-sm text-gray-700">Estimated processing time: ~5 minutes</p>
        </div>
        
        {/* Response Display */}
        {response && (
          <Alert className="border-green-200 bg-green-50">
            <div className="space-y-3">
              <AlertDescription className="text-sm">
                <strong>{response.message}</strong>
              </AlertDescription>
              
              <div className="text-sm text-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="h-4 w-4" />
                  <span className="font-medium">Intelligence report generation has been queued successfully.</span>
                </div>
                
                <div className="bg-green-100 rounded-md p-3 mb-3">
                  <div className="font-medium mb-1">AI Web Search Analysis:</div>
                  <div className="text-xs space-y-1">
                    <div>🔍 Web Search: Real-time market intelligence gathering</div>
                    <div>🧠 AI Analysis: Strategic insights with competitor analysis</div>
                    <div>📊 Board Report: Executive-ready strategic recommendations</div>
                  </div>
                </div>
                
                <div className="pl-6 space-y-1">
                  <p>🌐 Intelligence report uses AI-powered web search to gather current market data.</p>
                  <p>📧 The completed report will be delivered via notification once processing is finished.</p>
                  <p>⚡ This streamlined analysis provides real-time strategic insights in minutes, not hours.</p>
                </div>
              </div>
            </div>
          </Alert>
        )}
        
      </CardContent>
    </Card>
  );
}